// @ts-ignore
//------------------------------------------------------------
// File:      Schools.ts
// Author:    Tony Henderson
// Generator: ElevenFiftyNine Automated Tool Chain  
// Project:   https://github.com/BinaryBlox-Schedule-API
//------------------------------------------------------------

// Entity State: Schools Entity State 
import { createSlice, PayloadAction, createEntityAdapter, EntityState, EntityAdapter, EntitySelectors } from '@reduxjs/toolkit'
import { ApiEntity, ApiEntityState } from '../index';
import { entityRequests } from '../effects/Schools.effects';


/**
* Redux: Entity Interfaces
*/
export interface SchoolEntity extends ApiEntity {}   
export interface SchoolCohortEntity extends ApiEntity {}   
export interface SchoolDesignatedContactEntity extends ApiEntity {}   
export interface SchoolInformationEntity extends ApiEntity {}   

/**
* Redux: State Interfaces
*/
export interface SchoolState extends ApiEntityState<SchoolEntity> {}; 
export interface SchoolCohortState extends ApiEntityState<SchoolCohortEntity> {}; 
export interface SchoolDesignatedContactState extends ApiEntityState<SchoolDesignatedContactEntity> {}; 
export interface SchoolInformationState extends ApiEntityState<SchoolInformationEntity> {}; 


/**
* @summary State Interface SchoolsState.
* @name SchoolsState 
*/
export interface SchoolsState {
    school: SchoolState; 
    schoolCohort: SchoolCohortState; 
    schoolDesignatedContact: SchoolDesignatedContactState; 
    schoolInformation: SchoolInformationState; 
};  


/**
* Redux: Entity adapters
*/

/**
* @summary <SchoolEntity> Entity Adapter 
* @name schoolEntityAdapter
* @description Redux Entity Adapter for type <SchoolEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<SchoolEntity> 
*/
const schoolEntityAdapter: EntityAdapter<SchoolEntity> = createEntityAdapter<SchoolEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: schools => schools.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})
/**
* @summary <SchoolCohortEntity> Entity Adapter 
* @name schoolCohortEntityAdapter
* @description Redux Entity Adapter for type <SchoolCohortEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<SchoolCohortEntity> 
*/
const schoolCohortEntityAdapter: EntityAdapter<SchoolCohortEntity> = createEntityAdapter<SchoolCohortEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: schools => schools.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})
/**
* @summary <SchoolDesignatedContactEntity> Entity Adapter 
* @name schoolDesignatedContactEntityAdapter
* @description Redux Entity Adapter for type <SchoolDesignatedContactEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<SchoolDesignatedContactEntity> 
*/
const schoolDesignatedContactEntityAdapter: EntityAdapter<SchoolDesignatedContactEntity> = createEntityAdapter<SchoolDesignatedContactEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: schools => schools.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})
/**
* @summary <SchoolInformationEntity> Entity Adapter 
* @name schoolInformationEntityAdapter
* @description Redux Entity Adapter for type <SchoolInformationEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<SchoolInformationEntity> 
*/
const schoolInformationEntityAdapter: EntityAdapter<SchoolInformationEntity> = createEntityAdapter<SchoolInformationEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: schools => schools.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})

/**
* @summary Aggregate EntityAdapter Interface SchoolsEntityAdapterList.
* @name SchoolsEntityAdapterList 
*/
const schoolsEntityAdapterList = {
    school: schoolEntityAdapter,
    schoolCohort: schoolCohortEntityAdapter,
    schoolDesignatedContact: schoolDesignatedContactEntityAdapter,
    schoolInformation: schoolInformationEntityAdapter,
};  

/**
* @summary Initial state for SchoolState.
* @name initialSchoolState
* @description Initial state for SchoolState. 
* @return SchoolState populated with default data. 
*/ 
export const initialSchoolState: SchoolState = schoolEntityAdapter.getInitialState(<SchoolState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});

/**
* @summary Initial state for SchoolCohortState.
* @name initialSchoolCohortState
* @description Initial state for SchoolCohortState. 
* @return SchoolCohortState populated with default data. 
*/ 
export const initialSchoolCohortState: SchoolCohortState = schoolCohortEntityAdapter.getInitialState(<SchoolCohortState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});

/**
* @summary Initial state for SchoolDesignatedContactState.
* @name initialSchoolDesignatedContactState
* @description Initial state for SchoolDesignatedContactState. 
* @return SchoolDesignatedContactState populated with default data. 
*/ 
export const initialSchoolDesignatedContactState: SchoolDesignatedContactState = schoolDesignatedContactEntityAdapter.getInitialState(<SchoolDesignatedContactState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});

/**
* @summary Initial state for SchoolInformationState.
* @name initialSchoolInformationState
* @description Initial state for SchoolInformationState. 
* @return SchoolInformationState populated with default data. 
*/ 
export const initialSchoolInformationState: SchoolInformationState = schoolInformationEntityAdapter.getInitialState(<SchoolInformationState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});


/**
* @summary Initial state for SchoolsState.
* @name initialState
* @description Initial state for SchoolsState. 
* @return SchoolsState populated with default data. 
*/
export const initialState: SchoolsState =  <SchoolsState>{ 
    school: initialSchoolState,  
    schoolCohort: initialSchoolCohortState,  
    schoolDesignatedContact: initialSchoolDesignatedContactState,  
    schoolInformation: initialSchoolInformationState,  
};




/**
* @summary Entity Slice for entity Schools
* @name entitySlice
* @description Entity Slice for entity Schools to created CRUD features. 
* @return entity slice of given entity type.
*/
const entitySlice = createSlice({
      name: 'schoolsReducer',
      initialState,
      reducers: 
      {  
            get_School_GetSchoolBySchoolCode_Data(state: SchoolsState) {
                  state.school = { ...state.school, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [get_School_GetSchoolBySchoolCode_Data] recieved...`);
                  return state;
            },
            get_School_GetSchoolBySchoolCode_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [get_School_GetSchoolBySchoolCode_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        schoolEntityAdapter.addOne(state.school, entity); 

                  return state;
            },
            get_School_GetSchoolBySchoolCode_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [get_School_GetSchoolBySchoolCode_Data_Fault] recieved...`);
                  return state;  
            },
            get_School_GetSchoolsByUserEmail_Data(state: SchoolsState) {
                  state.school = { ...state.school, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [get_School_GetSchoolsByUserEmail_Data] recieved...`);
                  return state;
            },
            get_School_GetSchoolsByUserEmail_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [get_School_GetSchoolsByUserEmail_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        schoolEntityAdapter.addOne(state.school, entity); 

                  return state;
            },
            get_School_GetSchoolsByUserEmail_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [get_School_GetSchoolsByUserEmail_Data_Fault] recieved...`);
                  return state;  
            },
            update_School_Update_Data(state: SchoolsState) {
                  state.school = { ...state.school, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [update_School_Update_Data] recieved...`);
                  return state;
            },
            update_School_Update_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [update_School_Update_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.school = schoolEntityAdapter.updateOne(state.school, entity); 

                  return state;
            },
            update_School_Update_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.school = { ...state.school, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [update_School_Update_Data_Fault] recieved...`);
                  return state;  
            },
            get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data(state: SchoolsState) {
                  state.schoolCohort = { ...state.schoolCohort, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data] recieved...`);
                  return state;
            },
            get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolCohort = { ...state.schoolCohort, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        schoolCohortEntityAdapter.addOne(state.schoolCohort, entity); 

                  return state;
            },
            get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolCohort = { ...state.schoolCohort, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [get_SchoolCohort_GetSchoolCohortsBySchoolCode_Data_Fault] recieved...`);
                  return state;  
            },
            create_SchoolDesignatedContact_Create_Data(state: SchoolsState) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [create_SchoolDesignatedContact_Create_Data] recieved...`);
                  return state;
            },
            create_SchoolDesignatedContact_Create_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [create_SchoolDesignatedContact_Create_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.schoolDesignatedContact = schoolDesignatedContactEntityAdapter.addOne(state.schoolDesignatedContact, entity); 

                  return state;
            },
            create_SchoolDesignatedContact_Create_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [create_SchoolDesignatedContact_Create_Data_Fault] recieved...`);
                  return state;  
            },
            delete_SchoolDesignatedContact_Delete_Data(state: SchoolsState) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [delete_SchoolDesignatedContact_Delete_Data] recieved...`);
                  return state;
            },
            delete_SchoolDesignatedContact_Delete_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [delete_SchoolDesignatedContact_Delete_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.schoolDesignatedContact = schoolDesignatedContactEntityAdapter.removeOne(state.schoolDesignatedContact, entity.id); 

                  return state;
            },
            delete_SchoolDesignatedContact_Delete_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [delete_SchoolDesignatedContact_Delete_Data_Fault] recieved...`);
                  return state;  
            },
            getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data(state: SchoolsState) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data] recieved...`);
                  return state;
            },
            getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  state.schoolDesignatedContact = schoolDesignatedContactEntityAdapter.setAll(state.schoolDesignatedContact, entityList);

                  return state;
            },
            getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [getAll_SchoolDesignatedContact_GetSchoolDesignatedContactBySchoolCode_Data_Fault] recieved...`);
                  return state;  
            },
            update_SchoolDesignatedContact_Update_Data(state: SchoolsState) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [update_SchoolDesignatedContact_Update_Data] recieved...`);
                  return state;
            },
            update_SchoolDesignatedContact_Update_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [update_SchoolDesignatedContact_Update_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.schoolDesignatedContact = schoolDesignatedContactEntityAdapter.updateOne(state.schoolDesignatedContact, entity); 

                  return state;
            },
            update_SchoolDesignatedContact_Update_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [update_SchoolDesignatedContact_Update_Data_Fault] recieved...`);
                  return state;  
            },
            create_SchoolInformation_Create_Data(state: SchoolsState) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [create_SchoolInformation_Create_Data] recieved...`);
                  return state;
            },
            create_SchoolInformation_Create_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [create_SchoolInformation_Create_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.schoolInformation = schoolInformationEntityAdapter.addOne(state.schoolInformation, entity); 

                  return state;
            },
            create_SchoolInformation_Create_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [create_SchoolInformation_Create_Data_Fault] recieved...`);
                  return state;  
            },
            getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data(state: SchoolsState) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data] recieved...`);
                  return state;
            },
            getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  state.schoolInformation = schoolInformationEntityAdapter.setAll(state.schoolInformation, entityList);

                  return state;
            },
            getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [getAll_SchoolInformation_GetSchoolInformationBySchoolCode_Data_Fault] recieved...`);
                  return state;  
            },
            update_SchoolInformation_Update_Data(state: SchoolsState) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [update_SchoolInformation_Update_Data] recieved...`);
                  return state;
            },
            update_SchoolInformation_Update_Data_Success(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [update_SchoolInformation_Update_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  let entity:any = (entityList && entityList.length > 0) ? entityList.indexOf(1) : undefined;

                  if(entity != undefined)
                        state.schoolInformation = schoolInformationEntityAdapter.updateOne(state.schoolInformation, entity); 

                  return state;
            },
            update_SchoolInformation_Update_Data_Fault(state: SchoolsState, action: PayloadAction<any>) {
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [update_SchoolInformation_Update_Data_Fault] recieved...`);
                  return state;  
            },

/**
* Extended actions, non-service for local select & delete
**/
            selectValue_School_Data_Action(state: SchoolsState, action: PayloadAction<any>) { 
                  state.school = { ...state.school, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_School: ${action.payload}`);
                  return state;
            },
            clearValues_School_Data_Action(state: SchoolsState) { 
                  state.school = { ...state.school, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_School] clearing...`);
                  state.school = schoolEntityAdapter.removeAll(state.school); 

                  return state;
            },
            selectValue_SchoolCohort_Data_Action(state: SchoolsState, action: PayloadAction<any>) { 
                  state.schoolCohort = { ...state.schoolCohort, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_SchoolCohort: ${action.payload}`);
                  return state;
            },
            clearValues_SchoolCohort_Data_Action(state: SchoolsState) { 
                  state.schoolCohort = { ...state.schoolCohort, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_SchoolCohort] clearing...`);
                  state.schoolCohort = schoolCohortEntityAdapter.removeAll(state.schoolCohort); 

                  return state;
            },
            selectValue_SchoolDesignatedContact_Data_Action(state: SchoolsState, action: PayloadAction<any>) { 
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_SchoolDesignatedContact: ${action.payload}`);
                  return state;
            },
            clearValues_SchoolDesignatedContact_Data_Action(state: SchoolsState) { 
                  state.schoolDesignatedContact = { ...state.schoolDesignatedContact, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_SchoolDesignatedContact] clearing...`);
                  state.schoolDesignatedContact = schoolDesignatedContactEntityAdapter.removeAll(state.schoolDesignatedContact); 

                  return state;
            },
            selectValue_SchoolInformation_Data_Action(state: SchoolsState, action: PayloadAction<any>) { 
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_SchoolInformation: ${action.payload}`);
                  return state;
            },
            clearValues_SchoolInformation_Data_Action(state: SchoolsState) { 
                  state.schoolInformation = { ...state.schoolInformation, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_SchoolInformation] clearing...`);
                  state.schoolInformation = schoolInformationEntityAdapter.removeAll(state.schoolInformation); 

                  return state;
            },
    } 
}); 


const { reducer } = entitySlice;

const { actions } = entitySlice;

export { actions as entityActions, entityRequests, reducer as entityReducer, schoolsEntityAdapterList as entityAdapterList};
   